import { useState, useEffect, useRef, type FormEvent } from "react";
import {
  X,
  Mail,
  Lock,
  User as UserIcon,
  AlertCircle,
} from "lucide-react";
import { Button } from "./Button";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const { t } = useLanguage();
  const { signIn, signUp } = useAuth();
  const { showToast } = useToast();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (isSignUp) {
        if (!name.trim()) {
          setError(t("common.nameRequired"));
          setLoading(false);
          return;
        }

        const result = await signUp(
          email,
          password,
          name
        );

        if (result.error) {
          setError(result.error.message || t("common.failedCreateAccount"));
          setLoading(false);
          return;
        }

        // Success - show toast and wait for state to update, then close
        setLoading(false);
        showToast(t("login.signupSuccess"), "success");
        closeTimeoutRef.current = setTimeout(() => {
          onClose();
          resetForm();
        }, 500);
      } else {
        const result = await signIn(email, password);
        if (result.error) {
          setError(result.error.message || t("common.unexpectedError"));
          setLoading(false);
        } else {
          // Success - show toast and wait for state to update, then close
          setLoading(false);
          showToast(t("login.success"), "success");
          closeTimeoutRef.current = setTimeout(() => {
            onClose();
            resetForm();
          }, 500);
        }
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("common.unexpectedError");
      setError(errorMessage);
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setName("");
    setError(null);
    setIsSignUp(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200 my-auto max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-primary to-primary-dark p-6 text-white flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold">
              {isSignUp ? t("login.createAccount") : t("login.title")}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-smooth"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-white/90 text-sm">
            {isSignUp ? t("login.subtitle") : t("login.subtitle")}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {isSignUp && (
            <div className="space-y-2 ">
              <label className="block text-sm font-medium text-slate-900">
                {t("login.name")}
              </label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("login.namePlaceholder")}
                  required
                  className="w-full border border-slate-200 rounded-xl py-3 pl-11 pr-4 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth bg-white"
                />
              </div>
            </div>
          )}

          <div className="space-y-2 ">
            <label className="block text-sm font-medium text-slate-900">
              {t("login.email")}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full border border-slate-200 rounded-xl py-3 pl-11 pr-4 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth bg-white"
              />
            </div>
          </div>

          <div className="space-y-2 ">
            <label className="block text-sm font-medium text-slate-900">
              {t("login.password")}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full border border-slate-200 rounded-xl py-3 pl-11 pr-4 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth bg-white"
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-700/20 rounded-xl text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            className="w-full !py-3"
            disabled={loading}
          >
            {loading
              ? t("common.loading")
              : isSignUp
              ? t("login.signUp")
              : t("login.signIn")}
          </Button>

          {/* Toggle Sign Up / Sign In */}
          <div className="text-center text-sm">
            <span className="text-slate-500">
              {isSignUp ? t("login.hasAccount") : t("login.noAccount")}
            </span>{" "}
            <button
              type="button"
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-primary hover:underline font-medium"
            >
              {isSignUp ? t("login.signIn") : t("login.signUp")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
